import { HUD_DISPLAY_FY } from "@/data/manual-facts";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, CTABand } from "@/components/site/Blocks";
import { states } from "@/data/states";
import { buildHead } from "@/lib/seo-head";
import { GovernmentAffiliationNotice } from "@/components/site/GovernmentAffiliationNotice";
import heroFmrHub from "@/assets/hero/hero-fmr-hub.jpg.asset.json";

const HERO = {
  src: heroFmrHub.url,
  alt: "A glowing golden map of the United States hovering above a desk with printed county rent tables, a calculator, and a photo of a suburban rental home, in purple and gold lighting",
};

export const Route = createFileRoute("/hud-fair-market-rents/")({
  head: () => {
    const url = "/hud-fair-market-rents";
    const title = `${HUD_DISPLAY_FY} HUD Fair Market Rents by State | Simply Approved`;
    const description =
      `Browse ${HUD_DISPLAY_FY} HUD Fair Market Rents (FMR) by state. Every county's Section 8 40th-percentile rent, plus DSCR modeling for HAP-contract investors.`;
    return buildHead({
      title,
      description,
      url,
      image: HERO.src,
      imageAlt: HERO.alt,
      keywords:
        "HUD fair market rents 2026, FMR by state, Section 8 rent limits, HUD FMR lookup, 40th percentile rent, Section 8 voucher payment standard, HAP contract rent, HUD rent data by county, Section 8 investing rents, DSCR Section 8 loan, fair market rent calculator, HUD rent tables",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "HUD Fair Market Rents", url },
      ],
      service: { name: "HUD Fair Market Rent Lookup" },
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: `${HUD_DISPLAY_FY} HUD Fair Market Rents — All States`,
          description,
          creator: { "@type": "GovernmentOrganization", name: "U.S. Department of Housing and Urban Development" },
          license: "https://www.huduser.gov/portal/pdrdatas_landing.html",
          temporalCoverage: "2026",
        },
      ],
    });
  },
  component: FmrHub,
});

function FmrHub() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="HUD data"
        title={`${HUD_DISPLAY_FY} HUD Fair Market Rents`}
        subtitle="Pick a state to see every county's FMR, or use the interactive US map to sort by cash flow."
        image={HERO}
        primaryLabel="Start My Approval"
        primaryHref="/apply"
        secondaryLabel="See Today's Rates"
        secondaryHref="/rates"
      />
      <Section>
        <div className="mx-auto max-w-5xl">
          <h2 className="font-display text-2xl font-bold text-foreground">FMR by state</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Every state where we serve investors. Each page lists every county's 2026 Fair Market Rent from Studio to 4BR — the same
            numbers HUD publishes for Section 8 voucher payment standards. Lending is licensed in Florida and Colorado; LLC business-purpose loans in other states where permitted.
          </p>
          <ul className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {states.map((s) => (
              <li key={s.slug}>
                <Link
                  to="/hud-fair-market-rents/$state"
                  params={{ state: s.slug }}
                  className="block rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:border-brand hover:text-brand"
                >
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Section>
      <div className="container mx-auto max-w-7xl px-4 pb-10">
        <GovernmentAffiliationNotice />
      </div>

      <CTABand
        title="Have a Section 8 deal to price?"
        body="Send the address, bedrooms, and asking rent — we'll DSCR-underwrite against HUD FMR and reply with pricing feedback."
      />
    </SiteShell>
  );
}
